import { useState } from 'react';
import { ClientTranscript, Pagination } from '../types/client';

interface UseClientTranscriptsReturn {
  transcripts: ClientTranscript[];
  loading: boolean;
  loadingMore: boolean;
  pagination: Pagination;
  fetchTranscripts: (clientId: string, isLoadMore?: boolean) => Promise<void>;
  handleLoadMore: (clientId: string) => void;
  resetTranscripts: () => void;
}

export const useClientTranscripts = (): UseClientTranscriptsReturn => {
  const [transcripts, setTranscripts] = useState<ClientTranscript[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false
  });

  const fetchTranscripts = async (clientId: string, isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
        setTranscripts([]);
      } else {
        setLoadingMore(true);
      }
      
      const currentOffset = isLoadMore ? pagination.offset + pagination.limit : 0;
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: currentOffset.toString(),
      });
      
      const response = await fetch(`/api/clients/${clientId}/transcripts?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          transcripts: ClientTranscript[];
          pagination?: Pagination;
          error?: string;
        };
        
        if (data.success) {
          if (isLoadMore) {
            setTranscripts(prev => [...prev, ...data.transcripts]);
          } else {
            setTranscripts(data.transcripts);
          }
          
          if (data.pagination) {
            setPagination(data.pagination);
          }
        } else {
          throw new Error(data.error || 'Failed to load client transcripts');
        }
      } else {
        throw new Error('Failed to load client transcripts');
      }
    } catch (err) {
      console.error('Error fetching client transcripts:', err);
      if (!isLoadMore) {
        setTranscripts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = (clientId: string) => {
    if (!loadingMore && pagination.hasMore) {
      fetchTranscripts(clientId, true);
    }
  };

  const resetTranscripts = () => {
    setTranscripts([]);
    setPagination({
      total: 0,
      limit: 10,
      offset: 0,
      hasMore: false
    });
  };

  return {
    transcripts,
    loading,
    loadingMore,
    pagination,
    fetchTranscripts,
    handleLoadMore,
    resetTranscripts
  };
}; 